import { useEffect, useState } from "react";

export const PREVIEW_QUERY_KEY = "previewFor";
export const PREVIEW_QUERY_VALUE = "tour-step";
export const PREVIEW_TARGET_KEY = "targetId";
export const PREVIEW_SELECTOR_KEY = "selector";

export function isInTourPreview(): boolean {
  if (typeof window === "undefined") return false;
  const params = new URLSearchParams(window.location.search);
  return params.get(PREVIEW_QUERY_KEY) === PREVIEW_QUERY_VALUE;
}

// P23a: a preview pane targets EITHER an anchor (data-tour ID) OR
// a raw CSS selector — never both. We model that as a discriminated
// type so the highlighter never has to guess which one to honor.
type PreviewTarget =
  | { kind: "anchor"; id: string }
  | { kind: "selector"; selector: string }
  | null;

function readPreviewTargetFromUrl(): PreviewTarget {
  if (typeof window === "undefined") return null;
  const params = new URLSearchParams(window.location.search);
  if (params.get(PREVIEW_QUERY_KEY) !== PREVIEW_QUERY_VALUE) return null;
  const selector = params.get(PREVIEW_SELECTOR_KEY);
  if (selector) return { kind: "selector", selector };
  const id = params.get(PREVIEW_TARGET_KEY);
  if (id) return { kind: "anchor", id };
  return null;
}

interface RingRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

function safeFindAnchor(targetId: string): HTMLElement | null {
  // Attribute-selector value context: escape backslash and double-quote.
  try {
    const escaped = targetId.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
    return document.querySelector<HTMLElement>(`[data-tour="${escaped}"]`);
  } catch {
    return null;
  }
}

function safeFindBySelector(selector: string): HTMLElement | null {
  try {
    const el = document.querySelector(selector);
    return el instanceof HTMLElement ? el : null;
  } catch {
    return null;
  }
}

function findTarget(target: PreviewTarget): HTMLElement | null {
  if (!target) return null;
  if (target.kind === "anchor") return safeFindAnchor(target.id);
  return safeFindBySelector(target.selector);
}

function describeTarget(target: PreviewTarget): string {
  if (!target) return "";
  if (target.kind === "anchor") return `data-tour="${target.id}"`;
  return target.selector;
}

export function TourPreviewHighlighter() {
  const [target, setTarget] = useState<PreviewTarget>(() => readPreviewTargetFromUrl());
  const [rect, setRect] = useState<RingRect | null>(null);
  // P23a: surfaces selector parse errors back to the author. The
  // highlighter is the source of truth for "does this resolve?",
  // so the dialog doesn't need its own try/catch on every keystroke.
  const [selectorError, setSelectorError] = useState(false);

  useEffect(() => {
    function syncFromUrl() {
      setTarget(readPreviewTargetFromUrl());
    }
    function onMessage(e: MessageEvent) {
      // Trust check: only accept same-origin messages from our parent
      // (the admin step-editor dialog hosting this iframe).
      if (e.origin !== window.location.origin) return;
      if (e.source !== window.parent) return;
      if (!e.data || typeof e.data !== "object") return;

      // P23b Pass 4: bidirectional probe used by the tour audit page.
      // Parent sends `tour-preview:probe-targets` with a correlation id
      // and a list of targets; we resolve each one against the live
      // DOM (mirroring TourManager's resolution: anchor first, else
      // raw CSS selector inside try/catch) and reply with
      // `tour-preview:probe-result`. Keeps audit cost cheap — a single
      // round-trip per tour instead of one navigation per step.
      if (e.data.type === "tour-preview:probe-targets") {
        const requestId = typeof e.data.requestId === "string" ? e.data.requestId : null;
        const rawTargets = Array.isArray(e.data.targets) ? e.data.targets : null;
        if (!requestId || !rawTargets || !window.parent) return;
        const results = rawTargets.map((t: unknown) => {
          const obj = (t && typeof t === "object" ? t : {}) as Record<string, unknown>;
          const stepId = typeof obj.stepId === "string" ? obj.stepId : "";
          const anchorId = typeof obj.anchorId === "string" ? obj.anchorId : null;
          const selector = typeof obj.selector === "string" ? obj.selector : null;
          let found = false;
          if (anchorId) found = !!safeFindAnchor(anchorId);
          else if (selector) found = !!safeFindBySelector(selector);
          return { stepId, status: found ? "found" : "missing" } as const;
        });
        window.parent.postMessage(
          { type: "tour-preview:probe-result", requestId, results },
          window.location.origin,
        );
        return;
      }

      if (e.data.type !== "tour-preview:set-target") return;
      const nextSelector = typeof e.data.selector === "string" ? e.data.selector : null;
      const nextId = typeof e.data.targetId === "string" ? e.data.targetId : null;

      const url = new URL(window.location.href);
      url.searchParams.set(PREVIEW_QUERY_KEY, PREVIEW_QUERY_VALUE);
      url.searchParams.delete(PREVIEW_TARGET_KEY);
      url.searchParams.delete(PREVIEW_SELECTOR_KEY);

      let next: PreviewTarget = null;
      if (nextSelector) {
        url.searchParams.set(PREVIEW_SELECTOR_KEY, nextSelector);
        next = { kind: "selector", selector: nextSelector };
      } else if (nextId) {
        url.searchParams.set(PREVIEW_TARGET_KEY, nextId);
        next = { kind: "anchor", id: nextId };
      }
      window.history.replaceState({}, "", url.toString());
      setTarget(next);
    }
    window.addEventListener("popstate", syncFromUrl);
    window.addEventListener("message", onMessage);
    return () => {
      window.removeEventListener("popstate", syncFromUrl);
      window.removeEventListener("message", onMessage);
    };
  }, []);

  // Measure the ring around the targeted element.
  useEffect(() => {
    if (!target) {
      setRect(null);
      setSelectorError(false);
      return;
    }

    let scrolled = false;
    let mo: MutationObserver | null = null;
    let ro: ResizeObserver | null = null;

    const measure = () => {
      // Pre-flight: detect a malformed selector so we can render a
      // distinct error chip instead of the generic "waiting…" one.
      if (target.kind === "selector") {
        try {
          document.querySelector(target.selector);
          setSelectorError(false);
        } catch {
          setSelectorError(true);
          setRect(null);
          return;
        }
      } else {
        setSelectorError(false);
      }
      const el = findTarget(target);
      if (!el) {
        setRect(null);
        return;
      }
      if (!scrolled) {
        scrolled = true;
        el.scrollIntoView({ block: "center", inline: "center", behavior: "smooth" });
        if (typeof ResizeObserver !== "undefined") {
          ro = new ResizeObserver(() => measure());
          ro.observe(el);
        }
      }
      const r = el.getBoundingClientRect();
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    };

    measure();

    mo = new MutationObserver(() => measure());
    mo.observe(document.body, { childList: true, subtree: true });

    const onScrollOrResize = () => measure();
    window.addEventListener("resize", onScrollOrResize);
    window.addEventListener("scroll", onScrollOrResize, true);

    return () => {
      mo?.disconnect();
      ro?.disconnect();
      window.removeEventListener("resize", onScrollOrResize);
      window.removeEventListener("scroll", onScrollOrResize, true);
    };
  }, [target]);

  if (!isInTourPreview()) return null;

  if (!target) {
    return (
      <div
        className="fixed top-2 left-1/2 -translate-x-1/2 z-[9999] rounded-md bg-amber-500/95 px-3 py-1.5 text-xs font-medium text-white shadow-lg pointer-events-none"
        data-testid="text-preview-no-target"
      >
        Tour preview — pick a target
      </div>
    );
  }

  if (selectorError) {
    return (
      <div
        className="fixed top-2 left-1/2 -translate-x-1/2 z-[9999] rounded-md bg-destructive px-3 py-1.5 text-xs font-medium text-destructive-foreground shadow-lg pointer-events-none max-w-[80vw]"
        data-testid="text-preview-selector-invalid"
      >
        Invalid CSS selector: <code className="font-mono">{describeTarget(target)}</code>
      </div>
    );
  }

  if (!rect) {
    return (
      <div
        className="fixed top-2 left-1/2 -translate-x-1/2 z-[9999] rounded-md bg-amber-500/95 px-3 py-1.5 text-xs font-medium text-white shadow-lg pointer-events-none max-w-[80vw]"
        data-testid="text-preview-target-missing"
      >
        Waiting for <code className="font-mono">{describeTarget(target)}</code>…
      </div>
    );
  }

  const PAD = 6;
  return (
    <>
      <div
        className="fixed top-2 left-1/2 -translate-x-1/2 z-[9999] rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground shadow-lg pointer-events-none max-w-[80vw]"
        data-testid="text-preview-target-active"
      >
        Tour preview — <code className="font-mono">{describeTarget(target)}</code>
      </div>
      <div
        className="fixed pointer-events-none z-[9998] rounded-md ring-4 ring-primary ring-offset-2 ring-offset-background animate-pulse"
        data-testid="tour-preview-ring"
        style={{
          top: rect.top - PAD,
          left: rect.left - PAD,
          width: rect.width + PAD * 2,
          height: rect.height + PAD * 2,
          transition: "top 120ms, left 120ms, width 120ms, height 120ms",
        }}
      />
    </>
  );
}
